'use client';

import { useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import type { MediaModalTab } from '@/src/components/MediaModal';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useListingCTAAvailability } from '@/src/components/features/property/hooks/useListingCTAAvailability';
import { emitTrackHdpPhotoGalleryOpenEvent } from '@/src/analytics/hotpads/events';
import { getHaloPhotos, type HaloPhoto } from '@/src/components/features/property/lib/getHaloPhotos';
import type { ListingVideo } from '@/src/types/listingDetails.type';
import { PhotoGallery } from './PhotoGallery';
import { MediaModalActions } from './MediaModalActions';

const MediaModal = dynamic(() => import('@/src/components/MediaModal').then((mod) => mod.MediaModal));

type Props = {
  listing: PropertyDetailListing;
};

type TourUrlObject = {
  label?: string | null;
  url?: string;
};

/**
 * PhotoGallerySection — wraps PhotoGallery with the "view all" click
 * handlers. The "view all photos" / video / 3d-tour / street-view triggers
 * open the shared MediaModal so desktop and mobile use the same media viewer.
 */
export const PhotoGallerySection = ({ listing }: Props) => {
  const { hasCTAs } = useListingCTAAvailability(listing);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaTab, setMediaTab] = useState<MediaModalTab>('photos');
  const [initialPhotoIndex, setInitialPhotoIndex] = useState(0);
  const mediaTriggerRef = useRef<HTMLElement | null>(null);

  const displayName = (listing as unknown as { displayName?: string })?.displayName || '';
  const photos = getHaloPhotos(listing);
  const hasRealPhotos = photos.some((photo) => !photo.noPhoto);
  const details = (
    listing as unknown as {
      details?: {
        external3dTourUrls?: Array<TourUrlObject>;
        tourLabels?: Array<string>;
        tourUrls?: Array<string>;
      };
    }
  )?.details;
  const tours: Array<{ label: string; url: string }> = [];
  const seenTourUrls = new Set<string>();
  const addTour = (url: string | undefined, label?: string | null) => {
    if (!url || seenTourUrls.has(url)) return;
    seenTourUrls.add(url);
    tours.push({ label: label || '', url });
  };
  details?.tourUrls?.forEach((url, index) => addTour(url, details.tourLabels?.[index]));
  details?.external3dTourUrls?.forEach((tour) => addTour(tour.url, tour.label));
  const tourUrls = tours.map((tour) => tour.url);
  const tourLabels = tours.map((tour) => tour.label || '');
  const previewPhoto = (listing as { previewPhoto?: HaloPhoto })?.previewPhoto;
  const has3dTour = tourUrls.length > 0;
  const listingVideos = (listing as unknown as { listingVideos?: Array<ListingVideo> })?.listingVideos || [];
  const hasVideos = listingVideos.length > 0;
  const geo = (listing as unknown as { geo?: { lat?: number; lon?: number } })?.geo;
  const hideStreet = Boolean((listing as unknown as { address?: { hideStreet?: boolean } })?.address?.hideStreet);
  // Street view exposes the exact street the listing is on, so suppress it
  // for undisclosed-address listings.
  const hasStreetView = geo?.lat != null && geo?.lon != null && !hideStreet;
  const openMedia = (tab: MediaModalTab, photoIndex = 0) => {
    setMediaTab(tab);
    setInitialPhotoIndex(photoIndex);
    setMediaOpen(true);
  };

  const openPhotoMedia = (photoIndex = 0) => {
    if (!hasRealPhotos) return;
    emitTrackHdpPhotoGalleryOpenEvent({ listing, triggerLocation: 'home_details' });
    openMedia('photos', photoIndex);
  };

  return (
    <div
      onClickCapture={(event) => {
        if (event.target instanceof Element) {
          mediaTriggerRef.current = event.target.closest('button');
        }
      }}
    >
      <PhotoGallery
        photos={photos}
        displayName={displayName}
        onAllPhotosClick={() => openPhotoMedia()}
        onGalleryTap={(index) => openPhotoMedia(index)}
        onPhotoClick={(index) => openPhotoMedia(index)}
        has3dTour={has3dTour}
        hasVideos={hasVideos}
        hasStreetView={hasStreetView}
        on3dTourClick={() => openMedia('3dtour')}
        onVideosClick={() => openMedia('videos')}
        onStreetViewClick={() => openMedia('streetview')}
      />

      {mediaOpen ? (
        <MediaModal
          isOpen={mediaOpen}
          desktopActions={
            <MediaModalActions
              triggerLocation="home_details_media_gallery"
              actionSet="secondary"
              listing={listing}
              presentation="desktop"
            />
          }
          desktopFooter={
            !hasCTAs ? null : (
              <MediaModalActions
                triggerLocation="home_details_media_gallery"
                actionSet="primary"
                listing={listing}
                presentation="desktop"
              />
            )
          }
          lightboxDesktopActions={
            <MediaModalActions
              triggerLocation="home_details_photo_lightbox"
              actionSet="secondary"
              listing={listing}
              presentation="desktop"
            />
          }
          lightboxDesktopPrimaryActions={
            !hasCTAs ? null : (
              <MediaModalActions
                triggerLocation="home_details_photo_lightbox"
                actionSet="primary"
                listing={listing}
                presentation="desktop"
                primaryOrientation="vertical"
              />
            )
          }
          lightboxMobileActions={
            !hasCTAs ? null : (
              <MediaModalActions
                actionSet="primary"
                triggerLocation="home_details_photo_lightbox"
                listing={listing}
                presentation="mobile"
              />
            )
          }
          lightboxMobileSecondaryActions={
            <MediaModalActions
              actionSet="secondary"
              triggerLocation="home_details_photo_lightbox"
              listing={listing}
              presentation="mobile"
            />
          }
          onClose={() => setMediaOpen(false)}
          initialTab={mediaTab}
          initialPhotoIndex={initialPhotoIndex}
          photos={photos}
          displayName={displayName}
          has3dTour={has3dTour}
          hasVideos={hasVideos}
          hasStreetView={hasStreetView}
          tourUrls={tourUrls}
          tourLabels={tourLabels}
          previewPhoto={previewPhoto}
          videos={listingVideos}
          streetViewLat={geo?.lat}
          streetViewLon={geo?.lon}
          streetViewAddress={listing.address}
          mobileFooter={
            !hasCTAs ? null : (
              <MediaModalActions
                actionSet="primary"
                triggerLocation="home_details_media_gallery"
                listing={listing}
                presentation="mobile"
              />
            )
          }
          mobileSecondaryActions={
            <MediaModalActions
              actionSet="secondary"
              triggerLocation="home_details_media_gallery"
              listing={listing}
              presentation="mobile"
            />
          }
          returnFocusRef={mediaTriggerRef}
        />
      ) : null}
    </div>
  );
};
